import React from 'react';
import { motion } from 'framer-motion';
import { cn } from '../../../../utils/cn';

// Status configurations with premium styling
const STATUS_CONFIG = {
  active: {
    color: 'text-success-700',
    bg: 'bg-success-50',
    border: 'border-success-200',
    dot: 'bg-success-500',
    label: 'Active'
  },
  priority: {
    color: 'text-error-700', 
    bg: 'bg-error-50',
    border: 'border-error-200',
    dot: 'bg-error-500',
    label: 'Priority'
  },
  escalated: {
    color: 'text-warning-700',
    bg: 'bg-warning-50', 
    border: 'border-warning-200',
    dot: 'bg-warning-500',
    label: 'Escalated'
  },
  'ai-processing': {
    color: 'text-primary-700',
    bg: 'bg-primary-50',
    border: 'border-primary-200', 
    dot: 'bg-primary-500',
    label: 'AI Processing'
  },
  neutral: {
    color: 'text-neutral-600',
    bg: 'bg-neutral-50',
    border: 'border-neutral-200',
    dot: 'bg-neutral-400',
    label: 'Neutral'
  }
} as const;

// Channel configurations
const CHANNEL_CONFIG = {
  email: { icon: '✉️', color: 'text-primary-600', bg: 'bg-primary-50' },
  sms: { icon: '💬', color: 'text-success-600', bg: 'bg-success-50' },
  linkedin: { icon: '💼', color: 'text-primary-600', bg: 'bg-primary-50' },
  phone: { icon: '📞', color: 'text-warning-600', bg: 'bg-warning-50' }
} as const;

export interface ConversationCardData {
  id: string;
  contact: {
    name: string;
    email: string;
    avatar?: string;
    company?: string;
  };
  lastMessage: {
    content: string;
    timestamp: string;
    isOutgoing: boolean;
  };
  status: keyof typeof STATUS_CONFIG;
  channel: keyof typeof CHANNEL_CONFIG;
  messageCount: number;
  unreadCount?: number;
  tags?: string[];
  isStarred?: boolean;
  priority?: 'low' | 'medium' | 'high';
}

export interface ConversationCardProps {
  conversation: ConversationCardData;
  isSelected?: boolean;
  onClick?: () => void;
  onStarToggle?: (id: string) => void;
  className?: string;
}

/**
 * Premium ConversationCard - Inspired by Linear, Customer.io, Intercom
 * 
 * Features:
 * - Beautiful typography hierarchy
 * - Sophisticated micro-interactions
 * - Premium shadows and borders
 * - Smart truncation and spacing
 * - Status-aware styling
 * - Accessibility compliant
 */
export const ConversationCard: React.FC<ConversationCardProps> = ({
  conversation,
  isSelected = false,
  onClick,
  onStarToggle,
  className
}) => {
  const status = STATUS_CONFIG[conversation.status];
  const channel = CHANNEL_CONFIG[conversation.channel];

  const formatTime = (timestamp: string) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffInHours = (now.getTime() - date.getTime()) / (1000 * 60 * 60);
    
    if (diffInHours < 1) return 'now';
    if (diffInHours < 24) return `${Math.floor(diffInHours)}h`;
    if (diffInHours < 168) return `${Math.floor(diffInHours / 24)}d`;
    return date.toLocaleDateString();
  };

  const getInitials = (name: string) => {
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 4 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -4 }}
      whileHover={{ 
        y: -1,
        transition: { duration: 0.15, ease: "easeOut" }
      }}
      whileTap={{ scale: 0.995 }}
      onClick={onClick}
      className={cn(
        // Base styling
        "group relative",
        "p-4 rounded-lg",
        "border transition-all duration-200",
        "cursor-pointer",
        
        // Premium card styling
        "bg-white/80 backdrop-blur-sm",
        
        // State-dependent styling
        isSelected 
          ? [
              "border-primary-300 bg-primary-50/60",
              "shadow-[0_2px_8px_rgba(99,102,241,0.12)]",
              "ring-1 ring-primary-200"
            ]
          : [
              "border-neutral-200/60 hover:border-neutral-300",
              "hover:bg-white/90",
              "hover:shadow-[0_2px_12px_rgba(0,0,0,0.08)]"
            ],
        
        // Unread styling
        conversation.unreadCount && conversation.unreadCount > 0 && !isSelected && [
          "border-primary-200",
          "bg-primary-50/40"
        ],
        
        className
      )}
    >
      {/* Top Section - Contact & Metadata */}
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center space-x-3 min-w-0 flex-1">
          {/* Avatar */}
          <div className="relative flex-shrink-0">
            {conversation.contact.avatar ? (
              <img
                src={conversation.contact.avatar}
                alt={conversation.contact.name}
                className="w-10 h-10 rounded-full object-cover ring-2 ring-white shadow-sm"
              />
            ) : (
              <div className={cn(
                "w-10 h-10 rounded-full",
                "bg-gradient-to-br from-primary-500 to-primary-600",
                "flex items-center justify-center",
                "text-white text-sm font-medium",
                "ring-2 ring-white shadow-sm"
              )}>
                {getInitials(conversation.contact.name)}
              </div>
            )}
            
            {/* Status indicator */}
            <div className={cn(
              "absolute -bottom-0.5 -right-0.5",
              "w-3 h-3 rounded-full border-2 border-white",
              status.dot
            )} />
          </div>

          {/* Contact info */}
          <div className="min-w-0 flex-1">
            <div className="flex items-center space-x-2 mb-1">
              <h3 className={cn(
                "font-medium text-sm text-neutral-900",
                "truncate",
                isSelected && "text-primary-900"
              )}>
                {conversation.contact.name}
              </h3>
              
              {/* Channel indicator */}
              <div className={cn(
                "flex items-center justify-center",
                "w-5 h-5 rounded text-xs",
                channel.bg, channel.color
              )}>
                {channel.icon}
              </div>
            </div>

            {/* Company & email */}
            <div className="text-xs text-neutral-500 truncate">
              {conversation.contact.company 
                ? `${conversation.contact.company} • ${conversation.contact.email}`
                : conversation.contact.email
              }
            </div>
          </div>
        </div>

        {/* Metadata */}
        <div className="flex items-start space-x-2 flex-shrink-0 ml-3">
          {/* Star button */}
          {onStarToggle && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onStarToggle(conversation.id);
              }}
              className={cn(
                "p-1 rounded-md transition-colors",
                "opacity-0 group-hover:opacity-100",
                conversation.isStarred && "opacity-100",
                "hover:bg-neutral-100"
              )}
            >
              <span className={cn(
                "text-sm",
                conversation.isStarred ? "text-warning-500" : "text-neutral-400"
              )}>
                {conversation.isStarred ? '⭐' : '☆'}
              </span>
            </button>
          )}

          {/* Timestamp */}
          <div className="text-xs text-neutral-500 font-medium">
            {formatTime(conversation.lastMessage.timestamp)}
          </div>
        </div>
      </div>

      {/* Status & Tags Section */}
      <div className="flex items-center justify-between mb-3">
        {/* Status badge */}
        <div className={cn(
          "inline-flex items-center space-x-1.5",
          "px-2 py-1 rounded-md text-xs font-medium",
          status.bg, status.border, status.color,
          "border"
        )}>
          <div className={cn("w-1.5 h-1.5 rounded-full", status.dot)} />
          <span>{status.label}</span>
        </div>

        {/* Message count */}
        <div className="flex items-center space-x-2">
          {conversation.unreadCount && conversation.unreadCount > 0 && (
            <div className="bg-primary-600 text-white text-xs font-medium px-2 py-0.5 rounded-full min-w-[20px] text-center">
              {conversation.unreadCount > 99 ? '99+' : conversation.unreadCount}
            </div>
          )}
          
          <div className="text-xs text-neutral-500">
            {conversation.messageCount} message{conversation.messageCount !== 1 ? 's' : ''}
          </div>
        </div>
      </div>

      {/* Last Message Preview */}
      <div className="space-y-2">
        <div className="flex items-center space-x-2">
          <div className={cn(
            "text-xs font-medium",
            conversation.lastMessage.isOutgoing ? "text-primary-600" : "text-neutral-600"
          )}>
            {conversation.lastMessage.isOutgoing ? 'You:' : `${conversation.contact.name.split(' ')[0]}:`}
          </div>
        </div>
        
        <p className={cn(
          "text-sm text-neutral-700 line-clamp-2 leading-relaxed",
          isSelected && "text-primary-800"
        )}>
          {conversation.lastMessage.content}
        </p>
      </div>

      {/* Tags (if any) */}
      {conversation.tags && conversation.tags.length > 0 && (
        <div className="flex items-center space-x-1 mt-3 pt-3 border-t border-neutral-100">
          {conversation.tags.slice(0, 2).map((tag, index) => (
            <div
              key={index}
              className="px-2 py-1 bg-neutral-100 text-neutral-600 text-xs rounded-md font-medium"
            >
              {tag}
            </div>
          ))}
          {conversation.tags.length > 2 && (
            <div className="text-xs text-neutral-500">
              +{conversation.tags.length - 2} more
            </div>
          )}
        </div>
      )}

      {/* Selection indicator */}
      {isSelected && (
        <div className="absolute left-0 top-0 bottom-0 w-1 bg-primary-500 rounded-r-sm" />
      )}
    </motion.div>
  );
};

// Conversation Card Skeleton for loading states
export const ConversationCardSkeleton: React.FC<{
  className?: string;
}> = ({ className }) => (
  <div className={cn("w-full p-4 border border-neutral-100 rounded-xl", className)}>
    <div className="flex items-start space-x-3">
      {/* Avatar skeleton */}
      <div className="w-10 h-10 bg-neutral-200 rounded-full animate-pulse" />
      
      {/* Content skeleton */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between mb-2">
          <div className="h-4 bg-neutral-200 rounded animate-pulse w-24" />
          <div className="h-3 bg-neutral-200 rounded animate-pulse w-8" />
        </div>
        
        <div className="flex items-center space-x-2 mb-2">
          <div className="h-5 bg-neutral-200 rounded-full animate-pulse w-16" />
          <div className="h-5 bg-neutral-200 rounded-full animate-pulse w-12" />
        </div>
        
        <div className="space-y-1">
          <div className="h-3 bg-neutral-200 rounded animate-pulse w-full" />
          <div className="h-3 bg-neutral-200 rounded animate-pulse w-3/4" />
        </div>
      </div>
    </div>
  </div>
); 